<template>
  <div ref="rootElement">
    <template v-if="filterType !== 'home'">
      <div v-if="!isAppointmentOn || lpuWithAppointmentIds.includes(lpuAddress.lpu.id) || !selectedItem.isWorkplace">
        <div
          v-if="hasSlots && lpuAddress.appointment_on || !selectedItem.isWorkplace"
        >
          <div v-show="isRequestSlotsDone">
            <v-divider
              v-if="isMobile"
              class="my-3"
            />
            <LpuNotWorkingLabel
              v-if="appointmentType === 'express' && lpuWorkingText"
              :lpu-working-text="lpuWorkingText"
              class="mb-3"
            />
            <PartCalendar
              :lpu-id="lpuAddress.lpu.id"
              :appointment-type="appointmentType"
              :is-workplace="isWorkplace"
              :lpu-working-text="lpuWorkingText"
              :lpu-phone="lpuAddress.lpu.lpuphone?.phone || ''"
              @calendar-time-choice="handleCalendarTimeChoice"
            />
          </div>
          <div v-show="!isRequestSlotsDone">
            <v-sheet
              class="d-flex align-center justify-center"
              height="168"
            >
              <v-progress-circular
                color="primary"
                size="48"
                indeterminate
              />
            </v-sheet>
          </div>
          <div
            v-if="!isMobile && isUserStaff && lpuAddress.staff_appointment_type"
            class="staff-appointment-type"
          >
            <div class="staff-appointment-type__text">
              {{ lpuAddress.staff_appointment_type }}
            </div>
          </div>
        </div>
        <div
          v-else
        >
          <v-btn
            v-if="appointmentWillEnableTime"
            color="primary"
            disabled
          >
            {{ t('Запись включится', options) }}
            <time>{{ appointmentWillEnableTime }}</time>
          </v-btn>
          <template v-else-if="isAppointmentOn">
            <LpuNotWorkingLabel
              v-if="appointmentType === 'express' && lpuWorkingText"
              :lpu-working-text="lpuWorkingText"
              class="mb-3"
            />
            <PartCalendar
              v-if="appointmentType === 'express'"
              :lpu-id="lpuAddress.lpu.id"
              :appointment-type="appointmentType"
              :lpu-working-text="lpuWorkingText"
              :is-workplace="isWorkplace"
              :lpu-phone="lpuAddress.lpu.lpuphone?.phone || ''"
            />
            <CalendarStub v-else-if="!hasSlots" />
            <v-btn
              v-else
              color="primary"
              depressed
              block
            >
              {{ t('Запись на приём', options) }}
            </v-btn>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <LpuNotWorkingLabel
        v-if="lpuWorkingText"
        :lpu-working-text="lpuWorkingText"
      />
      <template v-if="isMobile && !lpuWorkingText">
        <div v-if="isPriceGoNumber && !lpuAddress.appointment_direct">
          <div class="text-body-2 text-info--text mb-4">
            {{ description }}
          </div>
          <v-btn
            color="primary"
            depressed
            block
            :href="`tel:${mobilePhone}`"
          >
            <v-icon class="mr-2">
              ui-icon-car-clinic
            </v-icon>
            {{ t('Вызвать', options) }} {{ t(doctor?.is_medic ? 'врача' : 'специалиста', options) }}
          </v-btn>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import I18NextVue from 'i18next'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { format, isValid, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import formatPhone from 'src/functions/formatPhone'
import { AppointmentModules } from 'www/modules/appointment-modules'
import { useDoctorAppointmentCardStore } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/stores'
import { PartCalendar } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/components/WorkplaceOrServiceCalendar/components'
import { useDoctorPageLpuListStore, useExternalDataStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import { LpuAddressList } from 'www/vue_components/common/DoctorPageLpuList/types'
import LpuNotWorkingLabel from 'www/vue_components/common/LpuNotWorkingLabel/LpuNotWorkingLabel.vue'
import CalendarStub
  from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/components/WorkplaceOrServiceCalendar/components/CalendarStub.vue'

const props = defineProps<{
  lpuAddress: LpuAddressList[number]
}>()

const { t, language } = I18NextVue
const options = { ns: language }
const description = t('Цена зависит от удалённости клиники. Уточните цену и способ оплаты во время звонка администратора.', options)

const rootElement = ref(null)

const doctorAppointmentCardStore = useDoctorAppointmentCardStore(props.lpuAddress.lpu.id)()
const {
  doctorId,
  isRequestSlotsDone,
  timedelta,
  selectedItem,
  redirectValue,
  calendarSlots,
} = storeToRefs(doctorAppointmentCardStore)

const externalDataStore = useExternalDataStore()
const {
  isAppointmentOn,
  lpuWithAppointmentIds,
  appointmentWillEnableTime,
  doctor,
  isMobile,
} = storeToRefs(externalDataStore)

const doctorPageLpuListStore = useDoctorPageLpuListStore()
const { filterType } = storeToRefs(doctorPageLpuListStore)

const appointmentModules = new AppointmentModules()

const appointmentType = computed(() => (
  selectedItem.value.workplace?.appointment_type || ''
))
const mobilePhone = computed(() => formatPhone({
  phone: props.lpuAddress.lpu.lpuphone?.phone,
  country: {
    iso: window.COUNTRY_DATA.iso,
  },
  isRaw: true,
}))
const isPriceGoNumber = computed(() =>
  // eslint-disable-next-line
   typeof props.lpuAddress.tabs_data?.price_go == 'number')
const lpuWorkingText = computed(() => {
  const date = selectedItem.value.workplace?.nearest_appointment_express_datetime || ''

  const parseISODateFormat = parseISO(date)

  if (!isValid(parseISODateFormat)) {
    return ''
  }

  const formattedDate = format(
    parseISODateFormat,
    'd\u00A0MMMM,\u00A0HH:mm',
    { locale: ru },
  )

  return `${t('Клиника закрыта, откроется', options)} ${formattedDate}`
})

const hasSlots = computed(() => {
  if (filterType.value === 'clinic') {
    if (props.lpuAddress.appointment_express && !props.lpuAddress.appointment_direct) {
      // Для ЭЗ выводится либо заглушка для слотов, либо кнопка записи (зависит от наличия слотов)
      return props.lpuAddress.has_slots_in_clinic
        && Object.keys(calendarSlots.value).some(date => calendarSlots.value[date].length)
    }
  }

  return props.lpuAddress.has_slots
})

const isWorkplace = computed(() => Boolean(selectedItem.value.isWorkplace))

function handleCalendarTimeChoice({ day, time }: { day: string, time: string }) {
  const slot = `${format(parseISO(day), 'dd.MM.yyyy', { locale: ru })} ${time}`
  const redirectData: Record<string, any> = {
    slot,
    lpu: props.lpuAddress.lpu.id,
    source: 'doctor-inner',
    timedelta: timedelta.value,
  }

  if (selectedItem.value.isWorkplace) {
    const type = redirectValue.value

    // @ts-ignore
    appointmentModules.redirectToNewAppointment({
      ...redirectData,
      doctor: doctorId.value,
      // @ts-ignore
      doctorSpec: selectedItem.value.workplace?.spec.id,
      type,
    })
  } else {
    redirectData.doctorId = doctorId.value
    redirectData.equipmentId = ''

    appointmentModules.redirectToNewAppointmentClinic({
      ...redirectData,
      targetDoctorId: doctorId.value,
      synonymId: selectedItem.value.service?.synonym_id,
    })
  }
}

const isUserStaff = window.USER.isStaff
</script>
